/**
 * Suchindex und Filterlogik gegen die 842 AVV-Abfallschluessel.
 *
 * Reines Datenmodul ohne React-Import -- die Darstellung (Combobox) kommt in Plan 26-04.
 * Die Suchsemantik ist woertlich aus dem abgenommenen Entwurf uebertragen:
 * .planning/sketches/004-avv-abfallschluessel/sketch004.html:236-285 (strip/expand/clean/
 * normA/normB/forms, IDX, score, parse, search) -- D-18 basiert exakt darauf. Einzige
 * bewusste Abweichung vom Entwurf: der `includes`-Zweig in scoreAvvItem bleibt erhalten
 * (D-24 "Ein Treffer mitten im Wort zaehlt, rankt aber schlechter als ein
 * Wortanfangstreffer" -- 26-CONTEXT.md).
 *
 * Backend-Validierung (AvvCatalog, AlternativeFuelWasteCodesValid, Plan 26-02) ist die
 * verbindliche Pruefung. Dieses Modul trifft keine Sicherheitsentscheidung, es ist reine
 * Such-/UX-Unterstuetzung.
 */

// Rohdaten: gleiche kanonische Liste wie backend/resources/avv/avv-abfallschluessel.json
// (Plan 26-01), generiert von app:avv:generate-catalog -- nicht von Hand pflegen.
import rawAvvCatalog from '../../data/avvAbfallschluessel.json';

interface AvvRawCatalog {
  meta: Record<string, unknown>;
  groupTitles: Record<string, string>;
  items: [code: string, title: string, hazardous: number, groupCode: string][];
}

const raw = rawAvvCatalog as unknown as AvvRawCatalog;

export interface AvvItem {
  code: string;
  title: string;
  hazardous: boolean;
  groupCode: string;
}

export interface AvvQuery {
  digits: string;
  words: string[][];
  plain: string[];
}

export interface AvvSearchResult {
  visible: AvvItem[];
  total: number;
  truncated: boolean;
}

/**
 * Gemeinsamer Vertrag fuer D-19 zwischen der Feldkomponente (Plan 26-04) und
 * BrennstoffeSection (Plan 26-06): `field` ist der Formularpfad des belegenden Feldes,
 * `label` die menschenlesbare Fundstelle ("Sekundärbrennstoffe, Zeile 4").
 */
export interface WasteCodeUsage {
  field: string;
  label: string;
}

// --- Modulkonstanten -----------------------------------------------------------------
// Einmal beim Modul-Load gebaut, niemals pro Render oder pro Aufruf (T-26-11, Pitfall 5).

export const AVV_ITEMS: readonly AvvItem[] = raw.items.map(([code, title, hazardous, groupCode]) => ({
  code,
  title,
  hazardous: hazardous === 1,
  groupCode,
}));

export const AVV_GROUP_TITLES: Readonly<Record<string, string>> = raw.groupTitles;

export const AVV_BY_CODE: ReadonlyMap<string, AvvItem> = new Map(
  AVV_ITEMS.map((item) => [item.code, item]),
);

/** Groessenordnung aus D-02 -- der exakte Wert ist Claude's Discretion. */
export const AVV_LIMIT = 50;

// --- Normalisierung (D-18: Umlaute in beiden Schreibweisen indiziert) -----------------
// Woertlich aus sketch004.html:237-242 uebertragen.

function strip(s: string): string {
  // Kombinierende diakritische Zeichen nach NFD-Zerlegung entfernen (z.B. o + Kombi-Umlaut -> o).
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '');
}

function expand(s: string): string {
  return s.replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss');
}

function clean(s: string): string {
  return s.replace(/[^a-z0-9]+/g, ' ').trim();
}

function normA(s: string): string {
  return clean(strip(s.toLowerCase()));
}

function normB(s: string): string {
  return clean(strip(expand(s.toLowerCase())));
}

/** Liefert das entdoppelte Formenpaar (Umlaut entfernt / Umlaut ausgeschrieben). */
function forms(s: string): string[] {
  return [...new Set([normA(s), normB(s)])];
}

// --- Suchindex -------------------------------------------------------------------------

const AVV_INDEX = new Map<string, { words: string[]; digits: string }>();
for (const item of AVV_ITEMS) {
  const src = [item.title, AVV_GROUP_TITLES[item.groupCode] ?? ''].join(' ');
  const words = new Set<string>();
  for (const form of forms(src)) {
    for (const word of form.split(' ')) {
      if (word) words.add(word);
    }
  }
  // Entfernt Leerzeichen UND den `*`-Marker gefaehrlicher Schluessel gleichermassen.
  AVV_INDEX.set(item.code, { words: [...words], digits: item.code.replace(/\D/g, '') });
}

/**
 * Zerlegt die Nutzereingabe in Ziffern- und Wort-Tokens (D-18).
 * Reine Ziffernfolgen ab drei Stellen gelten als Codefilter, alles andere als Suchwort.
 * `*` wird zu einem Trenner, nicht zu einem Suchwort (D-06-Schluessel wie `13 02 05*`).
 */
export function parseAvvQuery(raw: string): AvvQuery {
  const tokens = normA(raw.replace(/\*/g, ' '))
    .split(' ')
    .filter(Boolean);
  const digitTokens = tokens.filter((t) => /^\d+$/.test(t));
  const codeDigits = digitTokens.join('');
  const useCode = codeDigits.length >= 3;
  const wordTokens = tokens.filter((t) => !(useCode && /^\d+$/.test(t)) && t.length >= 2);

  return {
    digits: useCode ? codeDigits : '',
    words: wordTokens.map((t) => forms(t)),
    plain: wordTokens,
  };
}

/**
 * Bewertet einen Code gegen eine geparste Anfrage. Kein Treffer -> -1, leere Anfrage -> 0.
 * Ziffern: startsWith 0, includes +2, sonst -1. Woerter: exakt 0, startsWith +1,
 * includes +4 (D-24: Teilstring-Treffer zaehlt, rankt aber schlechter), sonst -1.
 */
export function scoreAvvItem(code: string, q: AvvQuery): number {
  if (!q.digits && !q.words.length) return 0;

  const ix = AVV_INDEX.get(code);
  if (!ix) return -1;

  let s = 0;

  if (q.digits) {
    if (ix.digits.startsWith(q.digits)) {
      s += 0;
    } else if (ix.digits.includes(q.digits)) {
      s += 2;
    } else {
      return -1;
    }
  }

  for (const variants of q.words) {
    if (variants.some((v) => ix.words.some((t) => t === v))) {
      s += 0;
    } else if (variants.some((v) => ix.words.some((t) => t.startsWith(v)))) {
      s += 1;
    } else if (variants.some((v) => ix.words.some((t) => t.includes(v)))) {
      s += 4;
    } else {
      return -1;
    }
  }

  return s;
}

/**
 * Liefert die abgeschnittene Trefferliste UND die echte Gesamttrefferzahl aus einem
 * einzigen Aufruf (D-02) -- die Abschneidung passiert HIER und nirgends sonst. Bei leerer
 * Eingabe bleiben alle Scores 0, der Zweitschluessel ist der Code: das liefert D-03 ("die
 * ersten 50 ab Kapitel 01") ohne Zusatzcode.
 */
export function avvVisibleItems(raw: string, limit: number = AVV_LIMIT): AvvSearchResult {
  const q = parseAvvQuery(raw);

  const scored = AVV_ITEMS.map((it) => ({ it, s: scoreAvvItem(it.code, q) }))
    .filter((r) => r.s >= 0)
    .sort((a, b) => a.s - b.s || a.it.code.localeCompare(b.it.code));

  const visible = scored.slice(0, limit).map((r) => r.it);

  return { visible, total: scored.length, truncated: scored.length > limit };
}

/**
 * Eine Folge aufeinanderfolgender Treffer derselben AVV-Gruppe.
 * `title` ist bereits aufgeloest, damit die Feldkomponente `AVV_GROUP_TITLES` nicht kennen muss.
 */
export interface AvvGroupRun {
  groupCode: string;
  title: string;
  items: AvvItem[];
}

/**
 * Zerlegt eine Trefferliste in Folgen aufeinanderfolgender Eintraege derselben Gruppe -- die
 * Grundlage dafuer, den Gruppentitel im Dropdown nur beim Wechsel zu zeigen statt an jeder
 * Zeile.
 *
 * Bewusst NUR aufeinanderfolgend: die Liste wird nicht umsortiert, die Reihenfolge nach
 * Treffergüte aus `avvVisibleItems` bleibt unangetastet. Dass das traegt, liegt am
 * Zweitschluessel der Sortierung (Code): innerhalb gleicher Treffergüte liegen die Gruppen
 * dadurch von selbst beieinander. Gemessen an den 842 Schluesseln fallen ohne Suche 41 von 50
 * Kontextzeilen weg, bei "kunststoff" 37 von 45, bei "öl" 25 von 50.
 *
 * Taucht dieselbe Gruppe an zwei getrennten Stellen der Liste auf, ergibt das zwei Folgen --
 * der Titel steht dann zweimal. Das ist gewollt: er beschreibt, was unter ihm steht.
 */
export function avvGroupRuns(items: readonly AvvItem[]): AvvGroupRun[] {
  const runs: AvvGroupRun[] = [];

  for (const item of items) {
    const current = runs[runs.length - 1];
    if (current && current.groupCode === item.groupCode) {
      current.items.push(item);
    } else {
      runs.push({
        groupCode: item.groupCode,
        title: AVV_GROUP_TITLES[item.groupCode] ?? item.groupCode,
        items: [item],
      });
    }
  }

  return runs;
}

/** Deutscher Zaehlertext fuer Combobox.Header (D-02). */
export function avvHitLabel(result: AvvSearchResult): string {
  if (result.truncated) {
    return `${result.visible.length} von ${result.total} angezeigt — Suche verfeinern`;
  }
  if (result.total === 0) {
    return 'Kein Schlüssel gefunden';
  }
  if (result.total === 1) {
    return '1 Treffer';
  }
  return `${result.total} Treffer`;
}
